import React, { useContext, useEffect, useState } from 'react';

import { Button, rem, type ButtonProps, type SystemStyleObject } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { useUpgradeTracking } from '@vimeo/picox';
import UpsellModal, { modalProps } from '@vimeo/upsell-modal';
import { ViewerContext } from '@vimeo/viewer';

import { useTracking } from '../../hooks/useTracking';
import { TrackingProps } from '../../interfaces';

export interface UpgradeBadgeProps extends Omit<ButtonProps, 'style'> {
  children?: string;
  href?: string;
  target?: HTMLAnchorElement['target'];
  noMargin?: boolean;
  size?: string;
  style?: SystemStyleObject;
  tracking?: TrackingProps;
  modalConfig?: modalProps['modalConfig'];
  modalTracking?: modalProps['tracking'];
  /** Name identifier for the upgrade button (for analytics) */
  name?: string;
  /** Location identifier for the upgrade button (for analytics) */
  location?: string;
}

const UpgradeBadge = ({
  children = translate('Upgrade'),
  href,
  target,
  onClick,
  noMargin = false,
  size = 'xs',
  style,
  tracking,
  modalConfig,
  modalTracking,
  name,
  location,
  ...buttonProps
}: UpgradeBadgeProps) => {
  const { sendUpsellEvent } = useTracking();
  const { trackUpgradeButtonClicked } = useUpgradeTracking();
  const viewer = useContext(ViewerContext);

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);

  const defaultProps = {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      // Track upgrade button click - derive button name from href if not provided
      const derivedName =
        name ??
        modalTracking?.paywallTracking?.paywallTrigger ??
        'upgrade_badge_unconfigured_button';
      const derivedLocation =
        location ??
        modalTracking?.paywallTracking?.paywallLocation ??
        (typeof window !== 'undefined' ? window.location.pathname : 'unknown');

      trackUpgradeButtonClicked({
        name: derivedName,
        location: derivedLocation,
        targetUrl: href ?? undefined,
      });

      if (tracking) {
        sendUpsellEvent({
          eventName: 'vimeo.trigger_upsell',
          isAction: true,
          ...tracking,
        });
      }

      if (modalConfig) {
        setIsUpsellModalOpen(true);
      }

      onClick?.(event);
    },
    variant: 'upsell',
    margin: noMargin ? 0 : `0 ${rem(8)}`,
    size: size,
    sx: {
      bgColor: 'upsell-primary',
      color: 'white',
      _loading: {
        bgColor: 'upsell-primary/50',
        _disabled: {
          bgColor: 'upsell-primary/50',
          _hover: {
            bgColor: 'red/50',
          },
        },
      },
      _disabled: {
        bgColor: 'upsell-primary',
        _hover: {
          bgColor: 'upsell-primary',
        },
      },
      _hover: {
        bgColor: 'purple.500',
      },
      _active: {
        bgColor: 'purple.500',
      },
      ...style,
    },
    ...buttonProps,
  };

  useEffect(() => {
    if (tracking) {
      sendUpsellEvent({
        eventName: 'vimeo.upsell_trigger_impression',
        isAction: false,
        ...tracking,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (modalConfig) {
    return (
      <>
        {isUpsellModalOpen && (
          <UpsellModal
            apiUrl={viewer?.apiUrl}
            userConfig={{ jwt: viewer?.jwt, userId: viewer?.user?.id }}
            templateType="default"
            modalConfig={modalConfig}
            onClose={() => setIsUpsellModalOpen(false)}
            tracking={
              modalTracking ?? {
                params: {
                  page: 'single_video_view',
                  feature: 'quota',
                  location: 'file_menu',
                  upsell_name: 'at_limit_quota_make_copy',
                },
                paywallTracking: {
                  paywallTrigger: 'upgrade_badge_unconfigured_button',
                  paywallLocation: 'single_video_view',
                  paywallType: 'popup',
                  paywallFeature: 'quota',
                },
              }
            }
          />
        )}
        <Button {...defaultProps}>{children}</Button>
      </>
    );
  } else if (onClick && !href) {
    return (
      <Button {...defaultProps} as="button">
        {children}
      </Button>
    );
  } else if (href) {
    return (
      <Button {...defaultProps} as="a" href={href} target={target || '_self'}>
        {children}
      </Button>
    );
  }

  return (
    <Button {...defaultProps} as="span">
      {children}
    </Button>
  );
};

export default UpgradeBadge;
export { UpgradeBadge as UpgradeButton };
